import { useEffect, useRef } from "react";
import { Info, X } from "lucide-react";
import type { UseExamSessionReturn } from "../../../lib/exam/useExamSession";

/**
 * "Overzicht examen" modal — pixel-faithful clone of real DUO grid popup.
 */
export type OverzichtModalProps = {
  open: boolean;
  onClose: () => void;
  session: UseExamSessionReturn;
  bookmarks?: Set<number>;
};

type CellState = "geselecteerd" | "bladwijzer" | "beantwoord" | "onbeantwoord";

export function OverzichtModal({
  open,
  onClose,
  session,
  bookmarks,
}: OverzichtModalProps) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) onClose();
  }

  function handleCancel(e: React.SyntheticEvent<HTMLDialogElement>) {
    e.preventDefault();
    onClose();
  }

  function cellStateFor(idx: number): CellState {
    if (idx === session.displayedIndex) return "geselecteerd";
    if (bookmarks?.has(idx)) return "bladwijzer";
    if (session.isAnswered(idx)) return "beantwoord";
    return "onbeantwoord";
  }

  function handleCellClick(idx: number) {
    session.jumpToIndex(idx);
    onClose();
  }

  return (
    <dialog
      ref={ref}
      data-skin="duo"
      onClick={handleBackdropClick}
      onCancel={handleCancel}
      aria-labelledby="overzicht-title"
      className="m-auto w-[640px] max-w-[calc(100%-2rem)] rounded-lg border-0 bg-[var(--duo-card)] p-0 text-[var(--duo-text)] shadow-[0_20px_25px_-5px_rgba(0,0,0,0.10)] backdrop:bg-black/50"
    >
      <div className="p-8">
        <div className="flex items-center justify-between">
          <h2 id="overzicht-title" className="text-[18px] font-semibold">
            Overzicht examen
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluit overzicht"
            className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-[var(--duo-text)] transition-colors hover:bg-[var(--duo-grey-cell)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2"
          >
            <X size={20} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </div>
        <div className="mt-6 grid grid-cols-8 gap-3">
          {session.questions.map((_, idx) => (
            <OverzichtCell
              key={idx}
              idx={idx}
              state={cellStateFor(idx)}
              onClick={() => handleCellClick(idx)}
            />
          ))}
        </div>
        <Legend />
      </div>
    </dialog>
  );
}

function OverzichtCell({
  idx,
  state,
  onClick,
}: {
  idx: number;
  state: CellState;
  onClick: () => void;
}) {
  const baseClass =
    "relative inline-flex h-14 w-14 items-center justify-center rounded-md text-[18px] font-semibold tabular-nums transition-colors hover:ring-2 hover:ring-[var(--duo-blue)] hover:ring-offset-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2";
  let stateClass = "";
  const label = `${idx + 1}`;
  if (state === "geselecteerd") {
    stateClass = "bg-[var(--duo-orange)] text-[var(--duo-orange-text)]";
  } else if (state === "beantwoord") {
    stateClass = "bg-[var(--duo-teal)] text-[#ffffff]";
  } else {
    stateClass =
      "bg-[var(--duo-grey-cell)] text-[var(--duo-grey-cell-text)]";
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Vraag ${idx + 1}, ${stateAriaLabel(state)}`}
      className={`${baseClass} ${stateClass}`}
    >
      {state === "bladwijzer" ? (
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--duo-info-pill)] text-[var(--duo-blue-text)]"
          aria-hidden="true"
        >
          <Info size={16} strokeWidth={2} aria-hidden="true" />
        </span>
      ) : (
        <span aria-hidden="true">{label}</span>
      )}
    </button>
  );
}

function stateAriaLabel(state: CellState): string {
  switch (state) {
    case "geselecteerd":
      return "geselecteerd";
    case "bladwijzer":
      return "bladwijzer";
    case "beantwoord":
      return "beantwoord";
    case "onbeantwoord":
      return "onbeantwoord";
  }
}

function Legend() {
  return (
    <ul className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-[var(--duo-text)]">
      <LegendRow swatchColor="var(--duo-orange)" label="Geselecteerd" />
      <LegendRow swatchColor="var(--duo-grey-cell)" label="Onbeantwoord" />
      <LegendRow swatchColor="var(--duo-teal)" label="Beantwoord" />
      <LegendRow
        swatchColor="var(--duo-info-pill)"
        label="Bladwijzers"
        usesIcon
      />
    </ul>
  );
}

function LegendRow({
  swatchColor,
  label,
  usesIcon,
}: {
  swatchColor: string;
  label: string;
  usesIcon?: boolean;
}) {
  return (
    <li className="inline-flex items-center gap-2">
      <span
        className="inline-flex h-3 w-3 items-center justify-center rounded-sm"
        style={{ background: swatchColor }}
        aria-hidden="true"
      >
        {usesIcon ? (
          <Info
            size={9}
            strokeWidth={3}
            color="#ffffff"
            aria-hidden="true"
          />
        ) : null}
      </span>
      <span>{label}</span>
    </li>
  );
}
